import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useReviewWorkspaceRole } from 'hooks/useWorkspace';
import cn from 'classnames';

import { ReactComponent as ThumbsUpIcon } from 'assets/thumb-icon.svg';
import { ReactComponent as ThumbsDownSelectedIcon } from 'assets/thumbs_down_selected.svg';
import { ReactComponent as ThumbsUpSelectedIcon } from 'assets/thumbs_up_selected.svg';

import { Button } from 'Components/Button';

import { setDesignReviewHistory } from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';

import DesignReviewService from 'services/designReview';
import { trackEvent } from 'services/eventTracking';

import { getBackendFeedbackId } from 'utlis/feedbackNumbering';

import styles from './styles.module.scss';

const getDeeperInsightsFeedback = record => (
  record?.extra_data?.post_processes?.deeper_insights?.feedback
);

const getFilteredFinalFeedbackLength = record => (
  (record?.final_feedback || []).filter(
    item => item.category !== 'deeper_insight' && !item.isDeeperInsight
  ).length
);

// Locate the array slot to update. Prefer backend feedback_id when present so
// display numbers never get used as mutation indexes.
const resolveFeedbackMutationTarget = (record, data, feedbackIndex) => {
  if (!record) {
    return null;
  }

  const backendId = getBackendFeedbackId(data);

  if (data.isDeeperInsight) {
    const extraList = getDeeperInsightsFeedback(record);

    if (Array.isArray(extraList) && extraList.length > 0) {
      if (backendId != null) {
        const byId = extraList.findIndex(item => getBackendFeedbackId(item) === backendId);

        if (byId >= 0) {
          return { isDeeperInsight: true, index: byId };
        }
      }

      const extraIndex = feedbackIndex - getFilteredFinalFeedbackLength(record);

      if (extraIndex >= 0 && extraIndex < extraList.length) {
        return { isDeeperInsight: true, index: extraIndex };
      }

      const legacyIndex = feedbackIndex - (record.final_feedback?.length || 0);

      if (legacyIndex >= 0 && legacyIndex < extraList.length) {
        return { isDeeperInsight: true, index: legacyIndex };
      }
    }
  }

  const finalList = record.final_feedback;

  if (!Array.isArray(finalList) || finalList.length === 0) {
    return null;
  }

  if (backendId != null) {
    const byId = finalList.findIndex(item => getBackendFeedbackId(item) === backendId);

    if (byId >= 0) {
      return { isDeeperInsight: false, index: byId };
    }
  }

  if (feedbackIndex >= 0 && feedbackIndex < finalList.length) {
    return { isDeeperInsight: false, index: feedbackIndex };
  }

  return null;
};

const LikeAndDismiss = ({ dismiss, currentFeedback, data, setErrorMsg, setShowFeedback, setDismiss }) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [thumbsUpEvent, setThumbsUp] = useState(data.thumbs_up ?? null);
  const [thumbsDownEvent, setThumbsDown] = useState(data.thumbs_down ?? null);

  const designReviewHistory = useSelector(state => state.designReview.designReviewHistory);
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);
  const authUser = useSelector(state => state.userStore);
  const { isReviewViewer } = useReviewWorkspaceRole(currentRecord);
  const isReviewOwner = !isReviewViewer && Boolean(authUser?.uuid && currentRecord?.owner?.uuid === authUser.uuid);

  const isFeedbackIncomplete
      = (thumbsUpEvent === null || thumbsUpEvent === undefined
        || thumbsDownEvent === null || thumbsDownEvent === undefined);

  // Source-list index (not the sorted display number shown on the card)
  const feedbackIndex = data.index !== undefined ? data.index : (currentFeedback - 1);

  if (!isReviewOwner) {
    return null;
  }

  return (
    <div className={cn(styles['bottom-container'], styles['footer'])}>
      <div className={styles['design-section-row']}>
        <div className={styles['design-section-helpful-box']}>
          {isFeedbackIncomplete
            ? (
              <>
                <ThumbsUpIcon
                  className={styles['thumbs-design']}
                  alt="thumbs Up"
                  onClick={() => handleReactReview('like')}
                />
                <ThumbsUpIcon
                  className={cn(styles['thumbs-design'], styles['rotate'])}
                  alt="thumbs Down"
                  onClick={() => handleReactReview('unlike')}
                />
              </>
            )
            : (
              <>
                {
                  thumbsUpEvent
                    ? (
                      <ThumbsUpSelectedIcon
                        className={styles['thumbs-design']}
                        onClick={() => handleReactReview(null)}
                      />
                    )
                    : (
                      <ThumbsDownSelectedIcon
                        className={styles['thumbs-design']}
                        onClick={() => handleReactReview(null)}
                      />
                    )
                }
              </>
            )}
        </div>
      </div>
      {isLoading
        ? <div className={styles['loader']} />
        : (
          <Button
            onClick={handleDismiss}
            className={cn(
              styles['dismiss-button']
            )}
          >
            {dismiss ? 'Put back' : 'Dismiss'}
          </Button>
        )}
    </div>
  );

  function handleReactReview(event) {
    const mutationTarget = resolveFeedbackMutationTarget(currentRecord, data, feedbackIndex);

    if (!mutationTarget) {
      setErrorMsg('Unable to perform action: Invalid feedback index or record.');

      return;
    }

    if (event) {
      trackEvent(`[click] ${event === 'like' ? 'like feedback' : 'unlike feedback'}`, {
        recordUpdated: currentRecord.uuid,
        feedbackUpdated: feedbackIndex,
      });
    }
    else {
      trackEvent(`[click] ${currentFeedback.thumbs_up ? 'undo like' : 'undo unlike'} `, {
        recordUpdated: currentRecord.uuid,
        feedbackUpdated: feedbackIndex,
      });
    }

    const thumbsUp = event === 'like' ? true : event === 'unlike' ? false : null;
    const thumbsDown = event === 'unlike' ? true : event === 'like' ? false : null;

    // Create a deep copy to avoid mutating Redux state
    const recordToUpdate = JSON.parse(JSON.stringify(currentRecord));

    if (mutationTarget.isDeeperInsight) {
      const deeperInsights = recordToUpdate.extra_data.post_processes.deeper_insights.feedback;
      const deeperInsightIndex = mutationTarget.index;

      recordToUpdate.extra_data.post_processes.deeper_insights.feedback[deeperInsightIndex] = {
        ...deeperInsights[deeperInsightIndex],
        thumbs_up: thumbsUp,
        thumbs_down: thumbsDown,
      };
    }
    else {
      recordToUpdate.final_feedback[mutationTarget.index] = {
        ...(recordToUpdate.final_feedback[mutationTarget.index] || {}),
        thumbs_up: thumbsUp,
        thumbs_down: thumbsDown,
      };
    }

    DesignReviewService.updateReview(recordToUpdate.uuid, recordToUpdate, setErrorMsg)
      .then(responseData => {
        if (responseData) {
          const mergedRecord = { ...currentRecord, ...responseData };

          dispatch(setCurrentRecord(mergedRecord));

          const reviewIndex = designReviewHistory?.results?.findIndex(val => val.uuid === currentRecord.uuid);

          if (reviewIndex !== -1 && designReviewHistory?.results) {
            const updatedReviewHistory = {
              ...designReviewHistory,
              results: [...designReviewHistory.results],
            };

            updatedReviewHistory.results[reviewIndex] = mergedRecord;
            dispatch(setDesignReviewHistory(updatedReviewHistory));
          }
          setThumbsUp(thumbsUp);
          setThumbsDown(thumbsDown);
          setErrorMsg('');
        }
      })
      .catch(error => {
        setErrorMsg(`Caught In Error: ${error.message || error}`);
        trackEvent('[error] unable to update like/unlike status', {
          recordUpdated: currentRecord.uuid,
          feedbackUpdated: feedbackIndex,
          error,
        });
      });
  }

  function handleDismiss() {
    const mutationTarget = resolveFeedbackMutationTarget(currentRecord, data, feedbackIndex);

    if (!mutationTarget) {
      setErrorMsg('Unable to perform action: Invalid feedback index or record.');

      return;
    }

    trackEvent(`[click] ${dismiss ? 'put back feedback' : 'dismiss feedback'}`, {
      recordUpdated: currentRecord.uuid,
      feedbackUpdated: feedbackIndex,
    });

    // Create a deep copy to avoid mutating Redux state
    const recordToUpdate = JSON.parse(JSON.stringify(currentRecord));

    if (mutationTarget.isDeeperInsight) {
      const deeperInsights = recordToUpdate.extra_data.post_processes.deeper_insights.feedback;
      const deeperInsightIndex = mutationTarget.index;

      recordToUpdate.extra_data.post_processes.deeper_insights.feedback[deeperInsightIndex] = {
        ...deeperInsights[deeperInsightIndex],
        dismiss: !dismiss,
      };
    }
    else {
      recordToUpdate.final_feedback[mutationTarget.index] = {
        ...(recordToUpdate.final_feedback[mutationTarget.index] || {}),
        dismiss: !dismiss,
      };
    }

    setIsLoading(true);
    DesignReviewService.updateReview(recordToUpdate.uuid, recordToUpdate, setErrorMsg)
      .then(responseData => {
        if (responseData) {
          const mergedRecord = { ...currentRecord, ...responseData };

          dispatch(setCurrentRecord(mergedRecord));

          const reviewIndex = designReviewHistory?.results?.findIndex(val => val.uuid === currentRecord.uuid);

          if (reviewIndex !== -1 && designReviewHistory?.results) {
            const updatedReviewHistory = {
              ...designReviewHistory,
              results: [...designReviewHistory.results],
            };

            updatedReviewHistory.results[reviewIndex] = mergedRecord;
            dispatch(setDesignReviewHistory(updatedReviewHistory));
          }

          setDismiss(!dismiss);
          setShowFeedback(dismiss);
          setErrorMsg('');
          setIsLoading(false);
        }
      })
      .catch(error => {
        setIsLoading(false);
        setErrorMsg(`Caught In Error: ${error.message || error}`);
        trackEvent(`[error] unable to ${dismiss ? 'put back' : 'dismiss'} feedback`, {
          recordUpdated: currentRecord.uuid,
          feedbackUpdated: feedbackIndex,
          error,
        });
      });
  }
};

LikeAndDismiss.propTypes = {
  dismiss: PropTypes.bool,
  currentFeedback: PropTypes.number,
  data: PropTypes.object,
  setErrorMsg: PropTypes.func,
  setShowFeedback: PropTypes.func,
  setDismiss: PropTypes.func,
};

export default LikeAndDismiss;
